import React from 'react';
import { useTranslation } from 'react-i18next';
import { Account, ConverterType } from '../../types';
import { useAccountConfiguration } from '../../hooks/useAccountConfiguration';
import { useCountriesAndBanks } from '../../hooks/useCountriesAndBanks';
import { BankDropdown } from '../';
import { bankIdToConverter } from '../../utils/converterResolution';

interface AccountSettingsModalProps {
  isVisible: boolean;
  onClose: () => void;
  account: Account | null;
  accountConfig: ReturnType<typeof useAccountConfiguration>;
  onConverterChange: (converter: ConverterType) => void;
  onUnlinkAccount?: (account: Account) => void;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  isVisible,
  onClose,
  account,
  accountConfig,
  onConverterChange,
  onUnlinkAccount
}) => {
  const { t } = useTranslation();
  const {
    countries,
    banks,
    selectedCountry,
    selectedBank,
    loading,
    error,
    setSelectedCountry,
    setSelectedBank,
  } = useCountriesAndBanks();

  // Reset dropdowns when modal opens for accounts with no configuration
  React.useEffect(() => {
    if (isVisible && account) {
      // Only select account if it's not already selected
      if (accountConfig.selectedAccountId !== account.id) {
        accountConfig.selectAccount(account);
      }
      
      const config = accountConfig.getAccountConfiguration(account);
      const hasConfiguration = config.selectedCountry || config.selectedBank || config.activeConverter;
      
      // If account has no configuration, reset the dropdowns
      if (!hasConfiguration) {
        setSelectedCountry(null);
        setSelectedBank(null);
      } else {
        // If account has configuration, restore it
        setSelectedCountry(config.selectedCountry || null);
        // Bank will be restored after country is loaded (see next useEffect)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: exclude accountConfig/setSelected* to avoid loops
  }, [isVisible, account?.id]);

  // Restore bank selection after country is loaded
  React.useEffect(() => {
    if (isVisible && account && selectedCountry) {
      const config = accountConfig.getAccountConfiguration(account);
      if (config.selectedBank && !selectedBank) {
        // Small delay to ensure banks are loaded for the selected country
        setTimeout(() => {
          setSelectedBank(config.selectedBank);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: exclude accountConfig/setSelectedBank to avoid loops
  }, [isVisible, selectedCountry, account?.id]);

  // Cleanup effect when modal closes
  React.useEffect(() => {
    if (!isVisible) {
      // Reset dropdowns when modal is not visible
      setSelectedCountry(null);
      setSelectedBank(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only run on visibility change
  }, [isVisible]);

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const countryId = event.target.value || null;
    setSelectedCountry(countryId);
    
    // Don't save immediately - only save when both country and bank are selected
    // Just ensure account is selected for later use
    if (account && accountConfig && accountConfig.selectedAccountId !== account.id) {
      accountConfig.selectAccount(account);
    }
  };

  const handleBankClick = (bankId: string) => {
    setSelectedBank(bankId);
    
    // Don't save immediately - only save when both country and bank are selected
    // Just ensure account is selected for later use
    if (account && accountConfig && accountConfig.selectedAccountId !== account.id) {
      accountConfig.selectAccount(account);
    }
  };

  // Helper function to get bank name from bank ID
  const getBankName = (bankId: string): string => {
    const bank = banks.find(b => b.id === bankId);
    return bank ? bank.name : bankId;
  };

  const handleSave = () => {
    // Save both country and bank configuration
    if (account && accountConfig && selectedCountry && selectedBank) {
      // Only select account if it's not already selected
      if (accountConfig.selectedAccountId !== account.id) {
        accountConfig.selectAccount(account);
      }

      const converter = bankIdToConverter(selectedBank);

      // Update the converter in the parent component
      onConverterChange(converter);
      
      // Save the configuration with bank name
      accountConfig.updateAccountConfiguration({
        selectedCountry: selectedCountry,
        selectedBank: selectedBank,
        selectedBankName: getBankName(selectedBank),
        activeConverter: converter,
      });
      
      console.log('✅ [Modal] Configuration saved successfully');
      onClose();
    } else {
      console.warn('⚠️ [Modal] Cannot save - both country and bank must be selected');
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleUnlink = () => {
    if (account && onUnlinkAccount) {
      // Clear the account configuration
      accountConfig.updateAccountConfiguration({
        selectedCountry: null,
        selectedBank: null,
        activeConverter: ''
      });
      onUnlinkAccount(account);
      onClose();
    }
  };

  // Check if account has complete configuration (both country and bank)
  const hasConfiguration = account && (() => {
    const config = accountConfig.getAccountConfiguration(account);
    return config.selectedCountry && config.selectedBank;
  })();

  // Check if both country and bank are selected for save button
  const canSave = selectedCountry && selectedBank;

  if (!isVisible || !account) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#212529' }}>
            {t('accountSettings', { ns: 'tools' })}: {account.name}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner" style={{ 
              display: 'inline-block', 
              width: '24px', 
              height: '24px', 
              border: '3px solid #ddd', 
              borderTop: '3px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p>{t('loading', { ns: 'tools' })}</p>
          </div>
        ) : error ? (
          <div style={{ color: '#dc3545', textAlign: 'center', padding: '1rem' }}>
            {t('error', { error, ns: 'tools' })}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="country-select" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                {t('selectCountry', { ns: 'tools' })}
              </label>
              <select
                id="country-select"
                value={selectedCountry || ''}
                onChange={handleCountryChange}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              >
                <option value="">{t('chooseCountry', { ns: 'tools' })}</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                {t('selectBank', { ns: 'tools' })}
              </label>
              <BankDropdown
                banks={banks}
                selectedBank={selectedBank}
                onBankSelect={handleBankClick}
              />
            </div>

            {selectedBank && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div className="bank-info">
                  {selectedBank === 'other' ? (
                    <div>
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>
                        {t('otherBanks', { ns: 'bankDropdown' })}
                      </p>
                      <small style={{ color: '#6c757d' }}>
                        AI will analyze your CSV file to automatically detect the bank statement format
                      </small>
                    </div>
                  ) : (
                    <>
                      <h4 style={{ margin: '0 0 0.5rem 0' }}>
                        {banks.find(b => b.id === selectedBank)?.name}
                      </h4>
                      <p style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>
                        {banks.find(b => b.id === selectedBank)?.description}
                      </p>
                      <div style={{ fontSize: '0.9rem' }}>
                        <strong>{t('supportedFormats', { ns: 'tools' })}</strong>
                        <span style={{ marginLeft: '0.5rem' }}>
                          {banks.find(b => b.id === selectedBank)?.fileTypes?.join(', ')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

                        <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {hasConfiguration && (
                <button
                  onClick={handleUnlink}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #dc3545',
                    backgroundColor: 'transparent',
                    color: '#dc3545',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc3545';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#dc3545';
                  }}
                >
                  {t('unlinkAccount', { ns: 'tools' })}
                </button>
              )}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={handleClose}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #6c757d',
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  {t('actions.cancel', { ns: 'common' })}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    backgroundColor: canSave ? '#007bff' : '#6c757d',
                    color: '#ffffff',
                    borderRadius: '6px',
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    fontSize: '1rem',
                    opacity: canSave ? 1 : 0.6
                  }}
                >
                  {t('actions.save', { ns: 'common' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettingsModal; 